import React from 'react';
import { X, ShieldCheck, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

interface VerificationHeaderBannerProps {
  isLoading: boolean;
  isVerified: boolean;
  error: string | null;
  onClose: () => void;
}

export const VerificationHeaderBanner: React.FC<VerificationHeaderBannerProps> = ({
  isLoading,
  isVerified,
  error,
  onClose,
}) => {
  return (
    <div
      className={`p-4 sm:p-6 border-b transition-colors relative overflow-hidden shrink-0 ${
        isVerified
          ? 'bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white'
          : error
          ? 'bg-gradient-to-r from-rose-600 to-red-700 text-white'
          : 'bg-gradient-to-r from-indigo-700 to-sky-700 text-white'
      }`}
    >
      {/* Background Pattern */}
      <div className="absolute -right-8 -bottom-8 opacity-10 pointer-events-none">
        <ShieldCheck className="w-56 h-56" />
      </div>

      {/* Close button top-right */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3.5 right-3.5 sm:top-5 sm:right-5 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition cursor-pointer z-20 shrink-0 shadow-xs"
        aria-label="Tutup Verifikasi"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="relative z-10 pr-8 sm:pr-10">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shrink-0 shadow-inner border border-white/30 mt-0.5">
            {isLoading ? (
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin text-white" />
            ) : isVerified ? (
              <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-200" />
            ) : error ? (
              <AlertCircle className="w-6 h-6 sm:w-7 sm:h-7 text-rose-200" />
            ) : (
              <ShieldCheck className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            )}
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/20 backdrop-blur-md text-[10px] sm:text-[11px] font-bold tracking-wide uppercase border border-white/20 max-w-full">
              <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
              <span className="whitespace-normal leading-tight">
                Sistem Registrasi UIBN Nasional
              </span>
            </div>

            <h2 className="text-base sm:text-2xl font-black tracking-tight leading-snug break-words">
              {isLoading
                ? 'Memeriksa Pangkalan Data...'
                : isVerified
                ? 'Buku Ini Terdaftar Resmi di UIBN'
                : error
                ? 'Verifikasi Tidak Ditemukan'
                : 'Status Verifikasi Naskah'}
            </h2>

            <p className="text-xs sm:text-sm text-white/90 font-medium leading-relaxed pt-0.5">
              {isVerified
                ? 'Katalog Universal Indonesian Book Number (UIBN) terverifikasi sah dan tercatat secara hukum dalam repositori nasional.'
                : error
                ? 'Nomor identifikasi atau buku yang diminta tidak tercatat dalam registri resmi UIBN.'
                : 'Meninjau berkas pendaftaran dan catatan silsilah buku.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
